import React, { useState, useEffect, useLayoutEffect, useRef, useCallback } from 'react';
import { useParams, Link, useNavigate, useLocation } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { Star, Heart, ShoppingCart, Truck, Shield, RotateCcw, ChevronRight, Minus, Plus, Play, Video, Store, CheckCircle2, Package, Info, ZoomIn, X, ChevronLeft, Pencil, Share2, Copy, Check, Link2 } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Separator } from '@/components/ui/separator';
import { Skeleton } from '@/components/ui/skeleton';
import { Card, CardContent } from '@/components/ui/card';
import { Checkbox } from '@/components/ui/checkbox';
import { ProductCard } from '@/components/products/ProductCard';
import ProductReviewForm, { ProductReviewLoginPrompt } from '@/components/products/ProductReviewForm';
import { products, reviews as reviewsApi, categories as categoriesApi, admin as adminApi } from '@/lib/api';
import { useCart } from '@/lib/cart-context';
import { collectVariantGroups, formatSelectedVariant, selectionFromProduct, variantKey } from '@/lib/variant';
import { useWishlist } from '@/lib/wishlist-context';
import { useAuth } from '@/lib/auth-context';
import { useCurrency } from '@/lib/currency-context';
import { formatDate } from '@/lib/utils';
import { MARKET_FALLBACK_IMAGE, normalizeProductImageSrc } from '@/lib/image-utils';
import { toast } from 'sonner';
import { Dialog, DialogContent } from '@/components/ui/dialog';
import { getPublicSiteUrl, absoluteImageUrl, stripHtmlForMeta, joinSitePath } from '@/lib/seo';
import { buildPathForLocale, getLocaleFromPathname } from '@/lib/intl-seo';
import { PageBreadcrumb } from '@/components/seo/PageBreadcrumb';
import { buildStorePath, getStoreSlugFromProduct } from '@/lib/store-url';
import { buildProductPath, isLegacyProductUrl } from '@/lib/product-url';
import { resolveProductSeoTitle, resolveProductSeoDescription } from '@/lib/product-seo';
import { renderSocialMetaTags } from '@/components/seo/SocialMetaTags';
import { GeoFaq } from '@/components/seo/GeoFaq';
import { productFaqItems } from '@/lib/geo-faq';
import { trackAiEvent } from '@/lib/ai-search-track';
import { shouldBlurSensitiveImage } from '@/lib/sensitive-content';

// Helper to convert YouTube/Vimeo URLs to embed URLs
const getVideoEmbedUrl = (url) => {
  if (!url) return null;
  
  // YouTube
  const youtubeMatch = url.match(/(?:youtube\.com\/(?:watch\?v=|embed\/)|youtu\.be\/)([a-zA-Z0-9_-]{11})/);
  if (youtubeMatch) {
    return `https://www.youtube.com/embed/${youtubeMatch[1]}`;
  }
  
  // Vimeo
  const vimeoMatch = url.match(/vimeo\.com\/(\d+)/);
  if (vimeoMatch) {
    return `https://player.vimeo.com/video/${vimeoMatch[1]}`;
  }
  
  // Direct video URL
  return url;
};

const isYouTubeOrVimeo = (url) => {
  return url && (url.includes('youtube') || url.includes('youtu.be') || url.includes('vimeo'));
};

/** Tek bir galeri öğesini (string veya { url }) normalize eder. */
const normalizeImageItem = (img) => {
  const asString =
    typeof img === 'string' ? img : (img?.url ?? img?.src ?? '');
  return normalizeProductImageSrc(asString);
};

const ProductDetailPage = () => {
  const { t, i18n } = useTranslation();
  const { id, lang } = useParams();
  const location = useLocation();
  const langCode = lang || getLocaleFromPathname(location.pathname);
  const { addItem, items } = useCart();
  const { isInWishlist, toggleWishlist } = useWishlist();
  const { user } = useAuth();
  const { formatPrice } = useCurrency();
  const navigate = useNavigate();
  const [product, setProduct] = useState(null);
  const [reviews, setReviews] = useState([]);
  const [relatedProducts, setRelatedProducts] = useState([]);
  const [frequentlyBoughtProducts, setFrequentlyBoughtProducts] = useState([]);
  const [customersAlsoViewedProducts, setCustomersAlsoViewedProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedImage, setSelectedImage] = useState(0);
  const [brokenImageIndices, setBrokenImageIndices] = useState({});
  const [selectedMedia, setSelectedMedia] = useState('image'); // 'image' or 'video'
  const [quantity, setQuantity] = useState(1);
  const [selectedBundleItems, setSelectedBundleItems] = useState([]);
  const [selectedVariant, setSelectedVariant] = useState(null);
  const [categoryInfo, setCategoryInfo] = useState(null);
  const [isSuperAdmin, setIsSuperAdmin] = useState(false);
  const [linkCopied, setLinkCopied] = useState(false);
  const [shareLoading, setShareLoading] = useState(false);
  const copyResetTimerRef = useRef(null);

  // Zoom states
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [sensitiveRevealedId, setSensitiveRevealedId] = useState(null);
  const [zoomPosition, setZoomPosition] = useState({ x: 0, y: 0 });
  const [isZooming, setIsZooming] = useState(false);
  const imageContainerRef = useRef(null);

  const sensitiveHidden = Boolean(product) && shouldBlurSensitiveImage(product) && sensitiveRevealedId !== product.id;

  const resolveDetailImage = useCallback(
    (index) => {
      const fixed = normalizeImageItem(product?.images?.[index]);
      if (!fixed || brokenImageIndices[index]) {
        return MARKET_FALLBACK_IMAGE;
      }
      return fixed;
    },
    [product, brokenImageIndices],
  );

  const handleDetailImageError = useCallback((index) => {
    setBrokenImageIndices((prev) => {
      if (prev[index]) return prev;
      return { ...prev, [index]: true };
    });
  }, []);

  // Wishlist handler
  const handleToggleWishlist = async () => {
    if (!user) {
      toast.error(t('wishlist.loginRequired') || 'Favorilere eklemek için giriş yapmalısınız');
      navigate('/login');
      return;
    }
    if (product) {
      await toggleWishlist(product.id, product.name);
    }
  };

  const isProductInWishlist = product ? isInWishlist(product.id) : false;

  const getProductShareUrl = useCallback(() => {
    if (product) {
      const siteBase = getPublicSiteUrl();
      if (siteBase) return `${siteBase}${buildProductPath(langCode, product)}`;
    }
    if (typeof window !== 'undefined') return window.location.href;
    return '';
  }, [product, langCode]);

  const handleCopyLink = useCallback(async () => {
    const shareUrl = getProductShareUrl();
    if (!shareUrl) return;
    try {
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(shareUrl);
      } else {
        window.prompt(t('product.copyLinkPrompt', 'Linki kopyalayın:'), shareUrl);
        return;
      }
      setLinkCopied(true);
      toast.success(t('product.linkCopied', 'Ürün linki kopyalandı'));
      if (copyResetTimerRef.current) clearTimeout(copyResetTimerRef.current);
      copyResetTimerRef.current = setTimeout(() => setLinkCopied(false), 2000);
    } catch {
      toast.error(t('product.shareError', 'Paylaşım başarısız'));
    }
  }, [getProductShareUrl, t]);

  const handleShareProduct = useCallback(async () => {
    const shareUrl = getProductShareUrl();
    if (!shareUrl) return;
    setShareLoading(true);
    try {
      const shareData = {
        title: product?.name || 'Turkishemarket',
        text: product?.name || '',
        url: shareUrl,
      };
      if (typeof navigator !== 'undefined' && typeof navigator.share === 'function') {
        await navigator.share(shareData);
      } else {
        await handleCopyLink();
      }
    } catch (err) {
      if (err?.name !== 'AbortError') {
        toast.error(t('product.shareError', 'Paylaşım başarısız'));
      }
    } finally {
      setShareLoading(false);
    }
  }, [getProductShareUrl, product, handleCopyLink, t]);

  useEffect(() => {
    return () => {
      if (copyResetTimerRef.current) clearTimeout(copyResetTimerRef.current);
    };
  }, []);

  useEffect(() => {
    if (user?.role !== 'admin') {
      setIsSuperAdmin(false);
      return undefined;
    }
    let cancelled = false;
    adminApi.getMyPermissions()
      .then((res) => {
        if (!cancelled) setIsSuperAdmin(!!res.data?.is_super_admin);
      })
      .catch(() => {
        if (!cancelled) setIsSuperAdmin(false);
      });
    return () => { cancelled = true; };
  }, [user]);

  const handleAdminEditProduct = (e) => {
    e.stopPropagation();
    if (!product?.id) return;
    navigate(`/admin?tab=products&productId=${product.id}&mode=edit`);
  };

  useLayoutEffect(() => {
    setProduct(null);
    setCategoryInfo(null);
    setReviews([]);
    setRelatedProducts([]);
    setFrequentlyBoughtProducts([]);
    setCustomersAlsoViewedProducts([]);
    setSelectedImage(0);
    setSelectedMedia('image');
    setBrokenImageIndices({});
  }, [id]);

  useEffect(() => {
    const ac = new AbortController();

    const fetchData = async () => {
      try {
        setLoading(true);
        const productRes = await products.get(id, { signal: ac.signal });
        if (ac.signal.aborted) return;

        let productData = productRes.data;

        const langKey = langCode === 'zh' ? 'zh-CN' : langCode;
        if (langKey && langKey !== 'tr' && productData.translations && productData.translations[langKey]) {
          const translation = productData.translations[langKey];
          if (translation.title) {
            productData.name = translation.title;
          } else if (translation.name) {
            productData.name = translation.name;
          }
          if (translation.description) {
            productData.description = translation.description;
          }
          if (translation.variants && Array.isArray(translation.variants)) {
            productData.variants = translation.variants;
          }
        }

        setProduct(productData);

        if (isLegacyProductUrl(id, productData)) {
          navigate(buildProductPath(langCode, productData), { replace: true });
          return;
        }

        try {
          const categoriesRes = await categoriesApi.list({ signal: ac.signal });
          if (ac.signal.aborted) return;
          const cats = categoriesRes.data || [];
          const slug = productData.category;
          if (slug) {
            const match = cats.find(
              (c) => c.slug === slug || c.id === productData.category_id,
            );
            setCategoryInfo(match || { name: String(slug), slug: String(slug) });
          } else {
            setCategoryInfo(null);
          }
        } catch {
          if (!ac.signal.aborted) setCategoryInfo(null);
        }

        try {
          const reviewsRes = await reviewsApi.get(productData.id);
          if (ac.signal.aborted) return;
          setReviews(reviewsRes.data);
        } catch (e) {
          if (!ac.signal.aborted) setReviews([]);
        }

        const productCategory = productData.category_id || productData.category;
        if (productCategory && !ac.signal.aborted) {
          const relatedRes = await products.list(
            {
              category: productCategory,
              limit: 8,
              lang: i18n.language,
            },
            { signal: ac.signal },
          );
          if (ac.signal.aborted) return;
          const filteredRelated = relatedRes.data.products.filter((p) => p.id !== productData.id);
          setRelatedProducts(filteredRelated.slice(0, 4));
          setFrequentlyBoughtProducts(filteredRelated.slice(0, 3));
          setSelectedBundleItems(filteredRelated.slice(0, 3).map((p) => p.id));
        }

        try {
          const alsoViewedRes = await products.list(
            { limit: 12, lang: i18n.language },
            { signal: ac.signal },
          );
          if (ac.signal.aborted) return;
          setCustomersAlsoViewedProducts(
            alsoViewedRes.data.products.filter((p) => p.id !== productData.id).slice(0, 6),
          );
        } catch (e) {
          if (!ac.signal.aborted) console.error('Failed to fetch customers also viewed:', e);
        }
      } catch (error) {
        if (error?.code !== 'ERR_CANCELED' && error?.name !== 'CanceledError') {
          console.error('Failed to fetch product:', error);
        }
      } finally {
        if (!ac.signal.aborted) {
          setLoading(false);
        }
      }
    };

    fetchData();
    return () => ac.abort();
  }, [id, lang, langCode, i18n.language, navigate]);

  const reloadReviews = useCallback(async () => {
    const pid = product?.id;
    if (!pid) return;
    try {
      const reviewsRes = await reviewsApi.get(pid);
      const data = Array.isArray(reviewsRes.data) ? reviewsRes.data : [];
      setReviews(data);
      if (data.length > 0) {
        const avg = data.reduce((s, r) => s + (Number(r.rating) || 0), 0) / data.length;
        setProduct((prev) =>
          prev
            ? { ...prev, rating: Math.round(avg * 10) / 10, review_count: data.length }
            : prev,
        );
      }
    } catch {
      /* keep existing list */
    }
  }, [product?.id]);

  const handleAddToCart = () => {
    const groups = collectVariantGroups(product);
    const missing = Object.keys(groups).filter((key) => !selectedVariant?.[key]);
    if (missing.length) {
      toast.error(t('product.selectVariant', 'Lütfen renk ve beden seçin'));
      return;
    }
    const chosen = selectionFromProduct(product, selectedVariant);
    addItem(product, quantity, chosen);
    trackAiEvent('add_to_cart', product.id);
    const variantText = formatSelectedVariant(chosen);
    toast.success(t('cart.addedToCart'), {
      description: variantText ? `${quantity}x ${product.name} (${variantText})` : `${quantity}x ${product.name}`,
      action: {
        label: t('cart.goToCart') || 'Sepete Git',
        onClick: () => navigate('/cart'),
      },
      duration: 5000,
    });
  };

  const toggleBundleItem = (productId) => {
    setSelectedBundleItems(prev => 
      prev.includes(productId) 
        ? prev.filter(id => id !== productId)
        : [...prev, productId]
    );
  };

  const calculateBundleTotal = () => {
    const mainProductPrice = product?.price || 0;
    const bundleProductsPrice = frequentlyBoughtProducts
      .filter(p => selectedBundleItems.includes(p.id))
      .reduce((sum, p) => sum + p.price, 0);
    return mainProductPrice + bundleProductsPrice;
  };

  const handleAddBundleToCart = () => {
    const groups = collectVariantGroups(product);
    const missing = Object.keys(groups).filter((key) => !selectedVariant?.[key]);
    if (missing.length) {
      toast.error(t('product.selectVariant', 'Lütfen renk ve beden seçin'));
      return;
    }
    const chosen = selectionFromProduct(product, selectedVariant);
    addItem(product, 1, chosen);
    // Add selected bundle products
    frequentlyBoughtProducts
      .filter(p => selectedBundleItems.includes(p.id))
      .forEach(p => addItem(p, 1));
    
    const itemCount = 1 + selectedBundleItems.length;
    toast.success(t('cart.addedToCart'), {
      description: `${itemCount} ${t('product.units')}`,
      action: {
        label: t('cart.goToCart') || 'Sepete Git',
        onClick: () => navigate('/cart'),
      },
      duration: 5000,
    });
  };

  // Zoom handlers
  const handleMouseMove = (e) => {
    if (!imageContainerRef.current) return;
    
    const rect = imageContainerRef.current.getBoundingClientRect();
    const x = ((e.clientX - rect.left) / rect.width) * 100;
    const y = ((e.clientY - rect.top) / rect.height) * 100;
    
    setZoomPosition({ x, y });
  };

  const handleMouseEnter = () => setIsZooming(true);
  const handleMouseLeave = () => setIsZooming(false);

  const handlePrevImage = () => {
    if (product?.images?.length > 0) {
      setSelectedImage((prev) => (prev === 0 ? product.images.length - 1 : prev - 1));
    }
  };

  const handleNextImage = () => {
    if (product?.images?.length > 0) {
      setSelectedImage((prev) => (prev === product.images.length - 1 ? 0 : prev + 1));
    }
  };

  const discount = product?.compare_price 
    ? Math.round((1 - product.price / product.compare_price) * 100) 
    : 0;

  if (loading) {
    return (
      <div className="py-8">
        <div className="container mx-auto px-4 md:px-8">
          <div className="grid md:grid-cols-2 gap-12">
            <Skeleton className="aspect-square rounded-2xl" />
            <div className="space-y-4">
              <Skeleton className="h-8 w-3/4" />
              <Skeleton className="h-6 w-1/4" />
              <Skeleton className="h-24 w-full" />
              <Skeleton className="h-12 w-full" />
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!product) {
    return (
      <div className="py-16 text-center">
        <h2 className="text-2xl font-bold">{t('product.notFound')}</h2>
        <Link to={buildPathForLocale(langCode, ['products'])}>
          <Button className="mt-4">{t('product.backToProducts')}</Button>
        </Link>
      </div>
    );
  }

  const site = getPublicSiteUrl();
  const path = buildProductPath(langCode, product);
  const canonical = site ? `${site}${path}` : '';
  const imagesLd = (product.images || [])
    .slice(0, 8)
    .map((img) => absoluteImageUrl(site, img))
    .filter(Boolean);
  const metaDesc = resolveProductSeoDescription(product, langCode);
  const seoProductTitle = resolveProductSeoTitle(product);
  const ogImage = imagesLd[0] || '';

  const avgRating =
    reviews.length > 0
      ? reviews.reduce((s, r) => s + (Number(r.rating) || 0), 0) / reviews.length
      : Number(product?.rating) || 0;
  const reviewCount = reviews.length > 0 ? reviews.length : Number(product?.review_count) || 0;
  const hasReviews = reviewCount > 0 && avgRating > 0;

  const breadcrumbJsonLd =
    site && canonical
      ? JSON.stringify({
          '@context': 'https://schema.org',
          '@type': 'BreadcrumbList',
          itemListElement: [
            {
              '@type': 'ListItem',
              position: 1,
              name: t('nav.home'),
              item: joinSitePath(site, buildPathForLocale(langCode, [])),
            },
            {
              '@type': 'ListItem',
              position: 2,
              name: t('nav.products'),
              item: joinSitePath(site, buildPathForLocale(langCode, ['products'])),
            },
            ...(categoryInfo?.slug
              ? [
                  {
                    '@type': 'ListItem',
                    position: 3,
                    name: categoryInfo.name,
                    item: joinSitePath(site, buildPathForLocale(langCode, ['category', categoryInfo.slug])),
                  },
                ]
              : []),
            {
              '@type': 'ListItem',
              position: categoryInfo?.slug ? 4 : 3,
              name: product.name,
              item: canonical,
            },
          ],
        })
      : '';

  const productBreadcrumbItems = [
    { label: t('nav.home'), to: buildPathForLocale(langCode, []) },
    { label: t('nav.products'), to: buildPathForLocale(langCode, ['products']) },
  ];
  if (categoryInfo?.slug) {
    productBreadcrumbItems.push({
      label: categoryInfo.name,
      to: buildPathForLocale(langCode, ['category', categoryInfo.slug]),
    });
  }
  productBreadcrumbItems.push({ label: product.name, to: null });

  return (
    <div
      className="py-4 bg-gray-50 overflow-x-hidden w-full max-w-full min-w-0"
      data-testid="product-detail-page"
      data-seo-ready="true"
    >
      <Helmet htmlAttributes={{ lang: langCode }}>
        <title>{seoProductTitle}</title>
        <meta name="description" content={metaDesc} />
        {canonical ? <link rel="canonical" href={canonical} /> : null}
        {renderSocialMetaTags({
          title: seoProductTitle,
          description: metaDesc,
          url: canonical,
          image: ogImage,
          type: 'product',
          locale: langCode,
        })}
        {breadcrumbJsonLd ? <script type="application/ld+json">{breadcrumbJsonLd}</script> : null}
      </Helmet>
      <div className="container mx-auto px-4 md:px-6 max-w-7xl w-full min-w-0 overflow-x-hidden">
        <PageBreadcrumb items={productBreadcrumbItems} className="text-xs" />

        {/* Main Product Layout - Hepsiburada Style */}
        <div className="grid lg:grid-cols-12 gap-4 min-w-0 w-full">
          {/* Left: Images - Smaller */}
          <div className="lg:col-span-5 min-w-0 w-full">
            <div className="bg-white rounded-lg p-3 shadow-sm max-w-full min-w-0">
              {/* Main Image - With Zoom */}
              <div 
                ref={imageContainerRef}
                className="relative aspect-square rounded-lg overflow-hidden bg-gray-100 mb-2 cursor-zoom-in group"
                onMouseMove={handleMouseMove}
                onMouseEnter={handleMouseEnter}
                onMouseLeave={handleMouseLeave}
                onClick={() => {
                  if (sensitiveHidden) return;
                  if (selectedMedia === 'image') setLightboxOpen(true);
                }}
              >
                {selectedMedia === 'video' && product.videos?.length > 0 ? (
                  <div className="w-full h-full">
                    {isYouTubeOrVimeo(product.videos[0]) ? (
                      <iframe
                        src={getVideoEmbedUrl(product.videos[0])}
                        className="w-full h-full"
                        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                        allowFullScreen
                        title="Product Video"
                      />
                    ) : (
                      <video src={product.videos[0]} controls className="w-full h-full object-cover" />
                    )}
                  </div>
                ) : (
                  <>
                    {/* Base Image */}
                    <img
                      src={resolveDetailImage(selectedImage)}
                      alt={product.name}
                      className={`w-full h-full max-w-full object-contain transition-opacity duration-200 ${sensitiveHidden ? 'scale-110 blur-2xl' : ''}`}
                      style={{ opacity: isZooming && !sensitiveHidden ? 0 : 1 }}
                      decoding="async"
                      fetchPriority={selectedImage === 0 ? 'high' : 'low'}
                      loading={selectedImage === 0 ? 'eager' : 'lazy'}
                      onError={() => handleDetailImageError(selectedImage)}
                    />
                    
                    {/* Zoomed Image (Desktop only) */}
                    {!sensitiveHidden && (
                    <div 
                      className="absolute inset-0 hidden md:block transition-opacity duration-200"
                      style={{
                        opacity: isZooming ? 1 : 0,
                        backgroundImage: `url(${resolveDetailImage(selectedImage)})`,
                        backgroundPosition: `${zoomPosition.x}% ${zoomPosition.y}%`,
                        backgroundSize: '200%',
                        backgroundRepeat: 'no-repeat',
                      }}
                    />
                    )}

                    {sensitiveHidden && (
                      <button
                        type="button"
                        className="absolute inset-0 z-20 flex items-center justify-center bg-neutral-900/40 text-white text-sm font-medium"
                        onClick={(e) => {
                          e.stopPropagation();
                          setSensitiveRevealedId(product.id);
                        }}
                      >
                        {t('product.showSensitiveImage', 'Görseli göster')}
                      </button>
                    )}
                    
                    {/* Zoom hint icon */}
                    <div className="absolute bottom-3 right-3 bg-black/50 text-white p-2 rounded-full opacity-0 group-hover:opacity-100 transition-opacity">
                      <ZoomIn className="w-5 h-5" />
                    </div>
                  </>
                )}
                
                {discount > 0 && (
                  <Badge className="absolute top-2 left-2 bg-red-600 text-white border-0 text-xs px-2 py-0.5">
                    -%{discount}
                  </Badge>
                )}

                {isSuperAdmin && (
                  <button
                    type="button"
                    onClick={handleAdminEditProduct}
                    className="absolute top-3 right-3 z-10 flex items-center gap-1.5 bg-primary text-primary-foreground px-3 py-1.5 rounded-full text-sm font-medium shadow-lg opacity-100 md:opacity-0 md:group-hover:opacity-100 transition-opacity hover:bg-primary/90"
                    data-testid="super-admin-edit-product"
                    title={t('seller.editProduct')}
                  >
                    <Pencil className="w-4 h-4" />
                    {t('seller.editProduct')}
                  </button>
                )}
              </div>
              
              {/* Thumbnails - Smaller */}
              <div className="flex gap-1.5 overflow-x-auto pb-1">
                {product.images?.map((img, i) => (
                  <button
                    key={`img-${i}`}
                    onClick={() => { setSelectedImage(i); setSelectedMedia('image'); }}
                    className={`flex-shrink-0 w-12 h-12 rounded border transition-colors ${
                      selectedMedia === 'image' && selectedImage === i ? 'border-orange-500 border-2' : 'border-gray-200'
                    }`}
                    data-testid={`image-thumb-${i}`}
                  >
                    <img
                      src={resolveDetailImage(i)}
                      alt=""
                      className={`w-full h-full object-cover rounded ${sensitiveHidden ? 'scale-125 blur-xl' : ''}`}
                      loading="lazy"
                      decoding="async"
                      onError={() => handleDetailImageError(i)}
                    />
                  </button>
                ))}
                {product.videos?.length > 0 && product.videos[0] && (
                  <button
                    onClick={() => setSelectedMedia('video')}
                    className={`flex-shrink-0 w-12 h-12 rounded border transition-colors relative bg-black ${
                      selectedMedia === 'video' ? 'border-orange-500 border-2' : 'border-gray-200'
                    }`}
                    data-testid="video-thumb"
                  >
                    <Play className="w-5 h-5 text-white absolute inset-0 m-auto" />
                  </button>
                )}
              </div>
            </div>
          </div>

          {/* Middle: Product Info */}
          <div className="lg:col-span-4 min-w-0 w-full">
            <div className="bg-white rounded-lg p-4 shadow-sm max-w-full min-w-0 overflow-hidden">
              {/* Brand */}
              {product.brand && (
                <Link
                  to={`${buildPathForLocale(langCode, ['products'])}?search=${encodeURIComponent(product.brand)}`}
                  className="text-xs text-orange-600 font-medium hover:underline mb-1 block"
                >
                  {product.brand}
                </Link>
              )}
              
              {/* Title + share / copy */}
              <div className="flex items-start justify-between gap-2 mb-2 min-w-0">
                <h1 className="text-base font-medium text-gray-900 leading-snug flex-1 min-w-0 break-words">{product.name}</h1>
                <div className="flex items-center gap-1 shrink-0 -mt-0.5">
                  <button
                    type="button"
                    onClick={handleShareProduct}
                    disabled={shareLoading}
                    data-testid="product-share-btn"
                    title={t('product.shareProduct', 'Ürünü paylaş')}
                    aria-label={t('product.shareProduct', 'Ürünü paylaş')}
                    className="inline-flex items-center justify-center w-8 h-8 rounded-full border border-gray-200 bg-white text-gray-600 hover:text-orange-600 hover:border-orange-200 hover:bg-orange-50 transition-colors disabled:opacity-50"
                  >
                    <Share2 className="w-3.5 h-3.5" />
                  </button>
                  <button
                    type="button"
                    onClick={handleCopyLink}
                    data-testid="product-copy-link-btn"
                    title={linkCopied ? t('product.linkCopied', 'Ürün linki kopyalandı') : t('product.copyLink', 'Linki kopyala')}
                    aria-label={t('product.copyLink', 'Linki kopyala')}
                    className="inline-flex items-center justify-center w-8 h-8 rounded-full border border-gray-200 bg-white text-gray-600 hover:text-orange-600 hover:border-orange-200 hover:bg-orange-50 transition-colors"
                  >
                    {linkCopied ? <Check className="w-3.5 h-3.5 text-green-600" /> : <Copy className="w-3.5 h-3.5" />}
                  </button>
                </div>
              </div>

              {/* Rating — only real customer reviews (no placeholder) */}
              <div className="flex items-center gap-2 mb-3 pb-3 border-b flex-wrap">
                {hasReviews ? (
                  <>
                    <div className="flex items-center bg-green-600 text-white px-1.5 py-0.5 rounded text-xs font-medium">
                      {Math.round(avgRating * 10) / 10} <Star className="w-3 h-3 ml-0.5 fill-current" />
                    </div>
                    <span className="text-xs text-gray-500">
                      {reviewCount} {t('product.reviews')}
                    </span>
                  </>
                ) : (
                  <span className="text-xs text-gray-500">{t('product.noReviewsYet', 'Henüz değerlendirme yok')}</span>
                )}
                {/* Made in Turkey Badge */}
                {product.made_in_turkey && (
                  <div className="flex items-center gap-1.5 bg-red-600 text-white px-3 py-1 rounded-full text-xs font-semibold ml-auto shadow-sm">
                    <span>🇹🇷</span>
                    <span>Made in Türkiye</span>
                  </div>
                )}
              </div>

              {/* Short Description */}
              {product.description && !product.descriptionNotTranslated && (
                <p className="text-xs text-gray-600 mb-3 line-clamp-3 break-words max-w-full">
                  {stripHtmlForMeta(product.description, 220)}
                </p>
              )}

              {/* Seller Info - Compact */}
              {(product.seller_id || product.seller_info || product.seller_name) && (() => {
                const storeSlug = getStoreSlugFromProduct(product)
                  || (product.seller_name ? product.seller_name.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '') : null);
                const storeName = product.seller_info?.store_name || product.seller_name || 'Turkish E-Market Seller';
                const storeHref = storeSlug ? buildStorePath(langCode, storeSlug) : null;
                return (
                  <div className="flex items-center gap-2 p-2 bg-gray-50 rounded-lg mb-3">
                    <Store className="w-4 h-4 text-gray-400 shrink-0" />
                    <div className="flex-1 min-w-0">
                      {storeHref ? (
                        <Link to={storeHref} className="text-xs font-medium text-primary hover:underline truncate block">
                          {storeName}
                        </Link>
                      ) : (
                        <p className="text-xs font-medium text-gray-900 truncate">{storeName}</p>
                      )}
                      {storeHref && (
                        <Link to={storeHref} className="text-[10px] text-primary hover:underline">
                          {t('store.visitStore', 'Mağazaya git')}
                        </Link>
                      )}
                    </div>
                  </div>
                );
              })()}

              {/* Stock Status */}
              <div className="flex items-center gap-2 text-xs mb-3">
                {product.stock > 0 ? (
                  <>
                    <CheckCircle2 className="w-4 h-4 text-green-600" />
                    <span className="text-green-600 font-medium">{t('product.inStock')}</span>
                    {product.stock < 10 && <span className="text-orange-500">{t('product.onlyLeft', { count: product.stock })}</span>}
                  </>
                ) : (
                  <>
                    <Package className="w-4 h-4 text-red-500" />
                    <span className="text-red-500 font-medium">{t('product.outOfStock')}</span>
                  </>
                )}
              </div>

              {/* SKU */}
              {product.sku && (
                <p className="text-[10px] text-gray-400">
                  {t('product.sku')}: <span className="font-mono">{product.sku}</span>
                </p>
              )}
            </div>
          </div>

          {/* Right: Price & Actions */}
          <div className="lg:col-span-3 min-w-0 w-full">
            <div className="bg-white rounded-lg p-4 shadow-sm sticky top-20 max-w-full min-w-0 overflow-hidden">
              {/* Price */}
              <div className="mb-4">
                {product.compare_price && (
                  <div className="flex items-center gap-2 mb-1">
                    <span className="text-sm text-gray-400 line-through">{formatPrice(product.compare_price, product.currency || 'TRY')}</span>
                    <Badge className="bg-red-100 text-red-600 border-0 text-[10px]">
                      %{discount} İndirim
                    </Badge>
                  </div>
                )}
                <div className="text-2xl font-bold text-orange-600">{formatPrice(product.price, product.currency || 'TRY')}</div>
                {product.tax_rate && (
                  <p className="text-[10px] text-gray-500 mt-0.5">KDV Dahil (%{product.tax_rate})</p>
                )}
              </div>

              {/* Variant Selection */}
              {(() => {
                const variantLabels = {
                  'color': t('product.color', 'Renk'), 'Color': t('product.color', 'Renk'), 'RENK': t('product.color', 'Renk'), 'renk': t('product.color', 'Renk'), 'Renk': t('product.color', 'Renk'),
                  'size': t('product.size', 'Beden'), 'Size': t('product.size', 'Beden'), 'BEDEN': t('product.size', 'Beden'), 'beden': t('product.size', 'Beden'), 'Beden': t('product.size', 'Beden'),
                };
                const getLabel = (key) => variantLabels[key] || key;
                const variantGroups = collectVariantGroups(product);
                const groupEntries = Object.entries(variantGroups);
                if (groupEntries.length === 0) return null;
                
                return (
                  <div className="mb-4 space-y-3" data-testid="variant-selection">
                    {groupEntries.map(([key, values]) => (
                      <div key={key}>
                        <span className="text-xs text-gray-600 block mb-1.5">{getLabel(key)}:</span>
                        <div className="flex flex-wrap gap-1.5">
                          {values.map(value => {
                            const isSelected = selectedVariant?.[key] === value;
                            // Check stock from variants or assume in stock
                            const variantInStock = product.variants?.length > 0
                              ? product.variants.some(v => (v[key] === value || v.value === value) && (v.stock === undefined || v.stock > 0))
                              : (product.stock > 0);
                            
                            return (
                              <button
                                key={value}
                                onClick={() => setSelectedVariant(prev => ({ ...prev, [key]: value }))}
                                disabled={!variantInStock}
                                data-testid={`variant-${key}-${value}`}
                                className={`px-3 py-1.5 text-xs border rounded-md transition-all ${
                                  isSelected 
                                    ? 'border-primary bg-primary/10 text-primary font-medium' 
                                    : variantInStock 
                                      ? 'border-gray-300 hover:border-primary' 
                                      : 'border-gray-200 text-gray-400 line-through cursor-not-allowed'
                                }`}
                              >
                                {value}
                              </button>
                            );
                          })}
                        </div>
                      </div>
                    ))}
                  </div>
                );
              })()}

              {/* Quantity */}
              <div className="flex items-center gap-2 mb-4">
                <span className="text-xs text-gray-600">{t('product.quantity')}:</span>
                <div className="flex items-center border rounded">
                  <Button
                    variant="ghost"
                    size="icon"
                    className="w-8 h-8 rounded-none"
                    onClick={() => setQuantity(Math.max(1, quantity - 1))}
                    disabled={quantity <= 1}
                    data-testid="qty-minus"
                  >
                    <Minus className="w-3 h-3" />
                  </Button>
                  <span className="w-10 text-center text-sm font-medium" data-testid="qty-value">{quantity}</span>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="w-8 h-8 rounded-none"
                    onClick={() => setQuantity(Math.min(product.stock, quantity + 1))}
                    disabled={quantity >= product.stock}
                    data-testid="qty-plus"
                  >
                    <Plus className="w-3 h-3" />
                  </Button>
                </div>
              </div>

              {/* Add to Cart Button */}
              {items.some((item) => item.product_id === product.id && variantKey(item.selected_variant) === variantKey(selectionFromProduct(product, selectedVariant))) ? (
                <Button
                  className="w-full bg-green-600 hover:bg-green-700 text-white mb-2 h-10"
                  onClick={() => navigate('/cart')}
                  data-testid="go-to-cart-btn"
                >
                  <ShoppingCart className="w-4 h-4 mr-2" />
                  {t('cart.goToCart')}
                </Button>
              ) : (
                <Button
                  className="w-full bg-orange-500 hover:bg-orange-600 text-white mb-2 h-10"
                  onClick={handleAddToCart}
                  disabled={product.stock === 0}
                  data-testid="add-to-cart-btn"
                >
                  <ShoppingCart className="w-4 h-4 mr-2" />
                  {t('product.addToCart')}
                </Button>
              )}

              {/* Wishlist + Share */}
              <div className="flex gap-2 mb-2">
                <Button 
                  variant={isProductInWishlist ? "default" : "outline"} 
                  className={`flex-1 h-9 text-sm ${isProductInWishlist ? 'bg-red-500 hover:bg-red-600 text-white' : ''}`}
                  onClick={handleToggleWishlist}
                  data-testid="wishlist-btn"
                >
                  <Heart className={`w-4 h-4 mr-1.5 ${isProductInWishlist ? 'fill-current' : ''}`} />
                  {isProductInWishlist ? t('wishlist.inWishlist', 'Favorilerde') : t('product.addToWishlist')}
                </Button>
                <Button
                  variant="outline"
                  size="icon"
                  className="h-9 w-9 shrink-0"
                  onClick={handleShareProduct}
                  disabled={shareLoading}
                  data-testid="product-share-action-btn"
                  title={t('product.shareProduct', 'Ürünü paylaş')}
                  aria-label={t('product.shareProduct', 'Ürünü paylaş')}
                >
                  <Share2 className="w-4 h-4" />
                </Button>
              </div>

              {/* Copy product link */}
              <div className="flex items-center gap-2 rounded-lg border border-gray-200 bg-gray-50 px-2.5 py-2 mb-4">
                <Link2 className="w-3.5 h-3.5 text-gray-400 shrink-0" />
                <span className="flex-1 min-w-0 truncate text-[11px] text-gray-500 font-mono" title={canonical || undefined}>
                  {(canonical || '').replace(/^https?:\/\//, '') || t('product.copyLink', 'Linki kopyala')}
                </span>
                <button
                  type="button"
                  onClick={handleCopyLink}
                  data-testid="product-copy-link-bar-btn"
                  className="inline-flex items-center gap-1 shrink-0 text-[11px] font-medium text-orange-600 hover:text-orange-700 transition-colors"
                >
                  {linkCopied ? (
                    <>
                      <Check className="w-3.5 h-3.5 text-green-600" />
                      <span className="text-green-600">{t('product.copied', 'Kopyalandı')}</span>
                    </>
                  ) : (
                    <>
                      <Copy className="w-3.5 h-3.5" />
                      {t('product.copyLink', 'Linki kopyala')}
                    </>
                  )}
                </button>
              </div>

              {/* Shipping & Features - Compact */}
              <div className="space-y-2 text-xs border-t pt-3">
                <div className="flex items-center gap-2 text-gray-600">
                  <Truck className="w-4 h-4 text-green-600" />
                  <span>{t('product.freeShipping')}</span>
                </div>
                <div className="flex items-center gap-2 text-gray-600">
                  <Shield className="w-4 h-4 text-blue-600" />
                  <span>{t('product.securePayment')}</span>
                </div>
                <div className="flex items-center gap-2 text-gray-600">
                  <RotateCcw className="w-4 h-4 text-orange-600" />
                  <span>{t('cart.returnGuarantee')}</span>
                </div>
              </div>

              {/* Tire Appointment Button - Only for tires category */}
              {(product.category === 'tires' || product.category_id === 'tires') && (
                <Link to={`/tire-appointment?tire_size=${encodeURIComponent(product.specifications?.Ebat || '')}`} className="block mt-3">
                  <Button 
                    variant="outline" 
                    className="w-full bg-green-50 border-green-300 text-green-700 hover:bg-green-100 h-9 text-xs"
                    data-testid="tire-appointment-btn"
                  >
                    Lastik Değişim Randevusu Al
                  </Button>
                </Link>
              )}
            </div>
          </div>
        </div>

        <div className="mt-4 bg-white rounded-lg shadow-sm overflow-hidden min-w-0 w-full max-w-full">
          <Tabs defaultValue="details" className="w-full">
            <TabsList className="w-full justify-start border-b rounded-none bg-transparent h-auto p-0 px-4 overflow-x-auto">
              <TabsTrigger 
                value="details" 
                className="rounded-none border-b-2 border-transparent data-[state=active]:border-orange-500 data-[state=active]:bg-transparent py-3 px-4 text-sm"
              >
                {t('product.description')}
              </TabsTrigger>
              <TabsTrigger 
                value="specs" 
                className="rounded-none border-b-2 border-transparent data-[state=active]:border-orange-500 data-[state=active]:bg-transparent py-3 px-4 text-sm"
              >
                {t('product.productDetails')}
              </TabsTrigger>
              <TabsTrigger 
                value="reviews" 
                className="rounded-none border-b-2 border-transparent data-[state=active]:border-orange-500 data-[state=active]:bg-transparent py-3 px-4 text-sm"
              >
                {t('product.reviewsCount', { count: reviews.length })}
              </TabsTrigger>
            </TabsList>

            <TabsContent value="details" className="p-4">
              {product.descriptionNotTranslated ? (
                <div className="text-center py-8 text-gray-500">
                  <p>{t('product.descriptionNotAvailable') || 'Description is being translated. Please check back later.'}</p>
                </div>
              ) : (
                <div 
                  className="prose prose-sm max-w-none text-sm text-gray-700 overflow-x-hidden break-words
                    [&_table]:w-full [&_table]:border-collapse [&_table]:my-2 [&_table]:table-fixed
                    [&_th]:bg-gray-100 [&_th]:p-2 [&_th]:text-left [&_th]:border [&_th]:border-gray-200 [&_th]:text-xs [&_th]:break-words
                    [&_td]:p-2 [&_td]:border [&_td]:border-gray-200 [&_td]:text-xs [&_td]:break-words
                    [&_img]:max-w-full [&_img]:h-auto
                    [&_pre]:overflow-x-auto [&_pre]:max-w-full
                    [&>*]:max-w-full"
                  dangerouslySetInnerHTML={{ __html: product.description || '' }}
                />
              )}
            </TabsContent>

            <TabsContent value="specs" className="p-4">
              <div className="grid md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  {product.brand && (
                    <div className="flex text-sm border-b pb-2">
                      <span className="w-1/2 text-gray-500">{t('product.brand')}</span>
                      <span className="w-1/2 font-medium">{product.brand}</span>
                    </div>
                  )}
                  {product.sku && (
                    <div className="flex text-sm border-b pb-2">
                      <span className="w-1/2 text-gray-500">{t('product.sku') || 'SKU'}</span>
                      <span className="w-1/2 font-medium font-mono">{product.sku}</span>
                    </div>
                  )}
                  {product.specifications && Object.entries(product.specifications).slice(0, 5).map(([key, value]) => (
                    <div key={key} className="flex text-sm border-b pb-2">
                      <span className="w-1/2 text-gray-500 capitalize">{key}</span>
                      <span className="w-1/2 font-medium">{value}</span>
                    </div>
                  ))}
                </div>
                <div className="space-y-2">
                  {product.specifications && Object.entries(product.specifications).slice(5).map(([key, value]) => (
                    <div key={key} className="flex text-sm border-b pb-2">
                      <span className="w-1/2 text-gray-500 capitalize">{key}</span>
                      <span className="w-1/2 font-medium">{value}</span>
                    </div>
                  ))}
                  <div className="flex text-sm border-b pb-2">
                    <span className="w-1/2 text-gray-500">{t('product.stockStatus') || 'Stock'}</span>
                    <span className={`w-1/2 font-medium ${product.stock > 0 ? 'text-green-600' : 'text-red-600'}`}>
                      {product.stock > 0 ? `${product.stock} ${t('product.units')}` : t('product.outOfStock')}
                    </span>
                  </div>
                </div>
              </div>
            </TabsContent>

            <TabsContent value="reviews" className="p-4">
              {user ? (
                <ProductReviewForm productId={product.id} onSubmitted={reloadReviews} />
              ) : (
                <ProductReviewLoginPrompt loginPath={langCode === 'en' ? '/login' : `/${langCode}/login`} />
              )}
              {reviews.length === 0 ? (
                <p className="text-sm text-gray-500 text-center py-8">{t('product.noReviews')}</p>
              ) : (
                <div className="space-y-4">
                  {reviews.map((review) => (
                    <div key={review.id} className="border-b pb-4 last:border-0">
                      <div className="flex items-center gap-2 mb-2">
                        <div className="flex items-center bg-green-600 text-white px-1.5 py-0.5 rounded text-xs">
                          {review.rating} <Star className="w-2.5 h-2.5 ml-0.5 fill-current" />
                        </div>
                        {review.is_verified_purchase && (
                          <Badge variant="secondary" className="text-[10px]">Onaylı Alışveriş</Badge>
                        )}
                        <span className="text-xs text-gray-400">{formatDate(review.created_at)}</span>
                      </div>
                      {review.title && <h4 className="text-sm font-medium mb-1">{review.title}</h4>}
                      <p className="text-sm text-gray-600">{review.comment}</p>
                      <p className="text-xs text-gray-400 mt-1">- {review.user_name}</p>
                    </div>
                  ))}
                </div>
              )}
            </TabsContent>
          </Tabs>
        </div>

        {/* Related Products - Compact */}
        {relatedProducts.length > 0 && (
          <div className="mt-4" data-testid="similar-products">
            <h2 className="text-base font-bold mb-3">{t('product.similarProducts')}</h2>
            <div className="grid grid-cols-3 sm:grid-cols-4 lg:grid-cols-6 gap-2 min-w-0">
              {relatedProducts.map((p) => (
                <ProductCard key={p.id} product={p} compact />
              ))}
            </div>
          </div>
        )}

        {/* Customers Also Viewed - Compact */}
        {customersAlsoViewedProducts.length > 0 && (
          <div className="mt-4" data-testid="customers-also-viewed">
            <h2 className="text-base font-bold mb-3">{t('product.customersAlsoViewed')}</h2>
            <div className="grid grid-cols-3 sm:grid-cols-4 lg:grid-cols-6 gap-2 min-w-0">
              {customersAlsoViewedProducts.map((p) => (
                <ProductCard key={p.id} product={p} compact />
              ))}
            </div>
          </div>
        )}

        <GeoFaq items={productFaqItems(product, t)} className="mt-8" />
      </div>

      {/* Lightbox / Full Screen Image Viewer */}
      <Dialog open={lightboxOpen} onOpenChange={setLightboxOpen}>
        <DialogContent className="max-w-[95vw] max-h-[95vh] w-full h-full p-0 bg-black/95 border-none">
          <div className="relative w-full h-full flex items-center justify-center">
            {/* Close button */}
            <button
              onClick={() => setLightboxOpen(false)}
              className="absolute top-4 right-4 z-50 p-2 bg-white/10 hover:bg-white/20 rounded-full transition-colors"
            >
              <X className="w-6 h-6 text-white" />
            </button>
            
            {/* Image counter */}
            <div className="absolute top-4 left-4 z-50 px-3 py-1 bg-white/10 rounded-full text-white text-sm">
              {selectedImage + 1} / {product?.images?.length || 1}
            </div>
            
            {/* Previous button */}
            {product?.images?.length > 1 && (
              <button
                onClick={handlePrevImage}
                className="absolute left-4 top-1/2 -translate-y-1/2 z-50 p-3 bg-white/10 hover:bg-white/20 rounded-full transition-colors"
              >
                <ChevronLeft className="w-8 h-8 text-white" />
              </button>
            )}
            
            {/* Main Image */}
            <img
              src={resolveDetailImage(selectedImage)}
              alt={product?.name}
              className={`max-w-full max-h-[85vh] object-contain select-none ${sensitiveHidden ? 'blur-2xl scale-110' : ''}`}
              draggable={false}
              onError={() => handleDetailImageError(selectedImage)}
            />
            
            {/* Next button */}
            {product?.images?.length > 1 && (
              <button
                onClick={handleNextImage}
                className="absolute right-4 top-1/2 -translate-y-1/2 z-50 p-3 bg-white/10 hover:bg-white/20 rounded-full transition-colors"
              >
                <ChevronRight className="w-8 h-8 text-white" />
              </button>
            )}
            
            {/* Thumbnail strip */}
            {product?.images?.length > 1 && (
              <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex gap-2 p-2 bg-white/10 rounded-lg backdrop-blur-sm">
                {product.images.map((img, index) => (
                  <button
                    key={index}
                    onClick={() => setSelectedImage(index)}
                    className={`w-16 h-16 rounded-md overflow-hidden border-2 transition-all ${
                      index === selectedImage 
                        ? 'border-white scale-110' 
                        : 'border-transparent opacity-60 hover:opacity-100'
                    }`}
                  >
                    <img
                      src={resolveDetailImage(index)}
                      alt={`${product.name} ${index + 1}`}
                      className={`w-full h-full object-cover ${sensitiveHidden ? 'blur-xl scale-125' : ''}`}
                      onError={() => handleDetailImageError(index)}
                    />
                  </button>
                ))}
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ProductDetailPage;
